import React from "react";
import { Box, IconButton, Modal, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Mic, MicOff, X } from "lucide-react";
import { AuroraBlob } from "./AuroraBlob";
import { Soft } from "../components/Common/Soft";
import { tokens } from "@/theme/tokens";
import type { VoiceSession } from "./useVoiceSession";

export interface VoiceModeProps {
  session: VoiceSession;
  onClose: () => void;
}

function statusText(s: VoiceSession): string {
  if (s.state === "connecting") return "Connecting…";
  if (s.state === "reconnecting") return "Reconnecting…";
  if (s.state === "error") return s.error ?? "Voice error";
  if (s.assistantSpeaking) return "Speaking…";
  return "Listening…";
}

/** Voice surface — a centered NEUMORPHIC card (matches the app's soft design),
 *  not a full-screen dark page. The parent provides a live `session`
 *  (useVoiceSession) and should have called connect(). Esc, backdrop click, or
 *  the end button tears down and closes.
 *
 *  MUI Modal provides: focus trap, focus restoration on close, aria-modal
 *  semantics, and onClose on Esc + backdrop click. */
export function VoiceMode({ session, onClose }: VoiceModeProps) {
  const endAndClose = async () => { await session.end(); onClose(); };
  const speaking = session.audioLevel > 0.15;

  return (
    <Modal
      open
      onClose={endAndClose}
      aria-labelledby="voice-mode-status"
      // Soft, theme-aware scrim instead of an opaque black takeover.
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: (t) =>
            alpha(t.palette.background.default, t.palette.mode === "light" ? 0.6 : 0.7),
          backdropFilter: "blur(4px)",
        },
      }}
    >
      <Soft
        variant="raised"
        role="dialog"
        aria-modal="true"
        aria-label="Voice mode"
        sx={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(420px, calc(100vw - 32px))",
          px: 3,
          py: 4,
          borderRadius: `${tokens.radius.lg}px`,
          color: "text.primary",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2.5,
          outline: "none",
        }}
      >
        <Typography
          id="voice-mode-status"
          aria-live="polite"
          variant="body2"
          sx={{ color: "text.secondary", fontWeight: 600 }}
        >
          {statusText(session)}
        </Typography>

        <AuroraBlob level={session.audioLevel} speaking={speaking} talking={session.assistantSpeaking} />

        <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
          {/* Mic toggle — flat disc, a real ring so it reads in dark mode. */}
          <IconButton
            onClick={() => session.toggleMute()}
            aria-label={session.isMuted ? "unmute" : "mute"}
            sx={(t) => {
              const dark = t.palette.mode === "dark";
              const ring = dark ? "rgba(255,255,255,0.09)" : "rgba(16,24,40,0.08)";
              return {
                width: 56,
                height: 56,
                bgcolor: "background.paper",
                border: `1px solid ${ring}`,
                color: session.isMuted ? "text.secondary" : "primary.main",
                boxShadow: session.isMuted ? "none" : tokens.elevation[1],
                "&:hover": { boxShadow: tokens.elevation[2], color: "primary.main" },
                "&:active": { boxShadow: tokens.elevation[1] },
              };
            }}
          >
            {session.isMuted ? <MicOff size={20} /> : <Mic size={20} />}
          </IconButton>

          {/* End — terracotta accent fill, visible in both themes regardless. */}
          <IconButton
            onClick={endAndClose}
            aria-label="end call"
            sx={{
              width: 56,
              height: 56,
              bgcolor: "tertiary.main",
              color: "tertiary.contrastText",
              boxShadow: tokens.elevation[1],
              "&:hover": { bgcolor: "tertiary.dark", boxShadow: tokens.elevation[2] },
              "&:active": { boxShadow: tokens.elevation[1] },
            }}
          >
            <X size={22} />
          </IconButton>
        </Box>
      </Soft>
    </Modal>
  );
}
